import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { partsService } from '../services/parts.service'

export const partsKeys = {
  all: ['parts'] as const,
  list: (params: any) => [...partsKeys.all, 'list', params] as const,
  categories: () => [...partsKeys.all, 'categories'] as const,
  detail: (slug: string) => [...partsKeys.all, 'detail', slug] as const,
}

export function useParts(params: Record<string, any> = {}) {
  return useQuery({
    queryKey: partsKeys.list(params),
    queryFn: async () => {
      const res = await partsService.getParts(params)
      return res.data
    },
  })
}

export function usePartCategories() {
  return useQuery({
    queryKey: partsKeys.categories(),
    queryFn: async () => {
      const res = await partsService.getCategories()
      return res.data ?? []
    },
  })
}

export function usePartDetail(slug: string) {
  return useQuery({
    queryKey: partsKeys.detail(slug),
    queryFn: async () => {
      const res = await partsService.getPartBySlug(slug)
      return res.data
    },
    enabled: Boolean(slug),
  })
}

export function useCreatePart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => partsService.createPart(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partsKeys.all })
    },
  })
}

export function useUpdatePart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => partsService.updatePart(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partsKeys.all })
    },
  })
}

export function useToggleFeaturedPart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => partsService.toggleFeatured(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partsKeys.all })
    },
  })
}

export function useDeletePart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => partsService.deletePart(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partsKeys.all })
    },
  })
}
